import React, { useEffect, useRef, useState } from "react";

import { MeasureCanvas } from "../../Text/TruncatedText/internal/MeasureCanvas";
import { buildFontTemplate, truncateText } from "../../Text/TruncatedText/internal/TruncatedTextUtils";
import SelectInput, { type BaseProps } from "./index";

// TruncatedSelectInput is a SelectInput that truncates the selected value if it is too long to fit in the container in two section
// The first section is the truncated text and the second section is a fixed text. Ex: "This is a long text... (Average)"
// The results props should have a value and a label and a fixed text to be displayed after the truncated text
const TruncatedSelectInput = ({ results, value, ...restProps }: BaseProps) => {
	const divRef = useRef<HTMLDivElement>(null);
	const [textWidth, setTextWidth] = useState(0);

	useEffect(() => {
		// Observe size changes
		const resizeObserver = new ResizeObserver((entries) => {
			const { width } = entries[0].contentRect;
			setTextWidth(width - 40); // 40 is the width of the caret
		});
		if (divRef.current) {
			resizeObserver.observe(divRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const formatOptionLabel = (option) => {
		const fontString = buildFontTemplate("paragraph-3", "regular");
		const measureCanvas = new MeasureCanvas();
		const fullWidth = measureCanvas.getTextWidth(`${option.label} ${option.fixedText}`, fontString);
		const fixedTextApproximateWidth = measureCanvas.getTextWidth(option.fixedText, fontString);
		const originalText = `${option.label} ${option?.fixedText ?? ""}`;

		if (fullWidth > textWidth - fixedTextApproximateWidth) {
			const truncatedText = truncateText(
				option.label,
				fontString,
				textWidth - fixedTextApproximateWidth,
				"...",
				measureCanvas
			);
			if (truncatedText !== "...") {
				return `${truncatedText} ${option?.fixedText ?? ""}`;
			}
			return originalText;
		}
		return originalText;
	};

	return (
		<div ref={divRef}>
			<SelectInput {...restProps} results={results} value={value} formatOptionLabel={formatOptionLabel} />
		</div>
	);
};

export default TruncatedSelectInput;
